/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The terms as a fact TABLE (D20): question · value, each value in its tone,
 * closed by when the redactie read the terms. The expanded row's panel, the
 * compare pages' blocks and the profile page's "Wat u moet weten" print this
 * ONE table, so a question reads the same everywhere. What the rows are is
 * decided by src/lib/termsPresentation.ts (`termsFactTable`); this component
 * words them (`offer_terms.*`) and paints them (./toneText.ts).
 *
 * No colour legend (D26, Jasper 2026-10-08: a legend in every card is clutter):
 * the colours are explained once, in the Voorwaarden column's note
 * (`table.col_terms_note`, ResultHeaderRow) and in the filter sheet's note
 * (`offer_terms.legend`, FilterSheet). The tone WORDS stay, for assistive tech.
 *
 * A `<dl>`: every `dd` is in its tone's colour AND spells the judgement out for
 * assistive tech ("(gunstig)" / "(let op)") — colour is never the only signal.
 * The smart-charger question is the modal's button (`aria-haspopup="dialog"`)
 * when the caller can open the modal (`onSmartInfo`), plain text otherwise. Its
 * accessible name is the VISIBLE question followed by the info sentence
 * ("Slimme laadpaal (Meer informatie over slimme laadpalen en MID-meters)"),
 * never an aria-label that drops the words on screen (WCAG 2.5.3) — the `dt` a
 * screen reader announces is still the question.
 *
 * What the table does NOT print as a row: the review ("Voorwaarden gelezen") —
 * the line under the table says it, in full — and, in the `panel` variant, the
 * looptijd (the row's own column shows it) and the price model (the commission
 * cell, its deviating-model icon and the verdienmodel aandachtspunt carry it; the profile page keeps
 * the row). Under a review that is not `read`
 * the binding questions are not in `rows` at all (the module omits them) and
 * the review sentence stands in for them at the TOP of the table, in its tone
 * ("De voorwaarden van {name} hebben wij nog niet gecontroleerd."); the payout,
 * inboeker, smart-charger, minimum and sign-up rows stay. Under `read` the
 * sentence closes the table: "Voorwaarden gelezen op 3 okt 2026."
 *
 * Use: `const table = termsFactTable({ dossier, …, providerName });` then
 * `<TermsFactTable {...table} providerName={name} variant="panel" linkSources={…}
 * onSmartInfo={…} keyPrefix={…} />` — `rows` and `review` are the module's own.
 *
 * Variants: `panel` is the Phase 0 composition A (13 px values on 20 px lines,
 * 12 px bold labels, 20 px between the columns — 14 px on a phone); `page` is
 * the profile page's scale (14 px, 16 px from md; labels over values below sm).
 *
 * `linkSources` is accepted so a surface hands the SAME value to all three terms
 * components; the table has no outbound link of its own (a fact's evidence stays
 * on the record and is never public, D2), so it changes nothing here.
 *
 * Pure markup — no state, no effect.
 */

import React from 'react';
import { cn } from '../../lib/cn';
import type { TermsFactRow, TermsReviewLine, TermsVar } from '../../lib/termsPresentation';
import { useTermsText } from './termsText';
import { TONE_TEXT } from './toneText';

export interface TermsFactTableProps {
  /** `termsFactTable(…).rows`: in question order, the binding ones absent unless the terms were read. */
  rows: readonly TermsFactRow[];
  /** `termsFactTable(…).review`: the review as one sentence. */
  review?: TermsReviewLine | null;
  /** The provider's name — fills the review sentence when its own `{name}` is empty. */
  providerName?: string;
  variant: 'panel' | 'page';
  /** See the header: the table prints no link, whatever this says. */
  linkSources: boolean;
  /** Opens the smart-charger modal: the question becomes its button. */
  onSmartInfo?: () => void;
  /** Stable prefix for the row keys when several tables render on one page. */
  keyPrefix: string;
  className?: string;
}

const DL: Record<TermsFactTableProps['variant'], string> = {
  // 13 px on 20 px lines; the columns 20 px apart (14 px on the phone), the rows 2 px
  panel:
    'grid grid-cols-[max-content_minmax(0,1fr)] gap-x-3.5 gap-y-0.5 text-[13px] leading-5 md:gap-x-5',
  // the labels sit over their values below sm
  page: 'grid grid-cols-1 gap-y-0.5 text-sm sm:grid-cols-[max-content_minmax(0,1fr)] sm:items-baseline sm:gap-x-6 sm:gap-y-2.5 md:text-base',
};

const DT: Record<TermsFactTableProps['variant'], string> = {
  panel: 'text-xs font-bold text-fg-muted',
  page: 'mt-3 text-xs font-bold text-fg-muted first:mt-0 sm:mt-0 md:text-sm',
};

/** Questions the expanded row already answers elsewhere on the row. */
const PANEL_OMITS: ReadonlySet<TermsFactRow['question']> = new Set(['duration', 'priceModel']);

const DD = 'min-w-0 break-words font-semibold';

const NOTE = 'font-medium text-fg-muted';

export const TermsFactTable: React.FC<TermsFactTableProps> = ({
  rows,
  review,
  providerName,
  variant,
  onSmartInfo,
  keyPrefix,
  className,
}) => {
  const { t, text, keyText, toneWord } = useTermsText();
  const page = variant === 'page';

  // The review is a LINE here, never a row; in the panel the row's own looptijd
  // column, its commission cell and the verdienmodel aandachtspunt already say the rest.
  const visible = rows.filter(
    (row) =>
      row.question !== 'termsReview' && !(variant === 'panel' && PANEL_OMITS.has(row.question))
  );

  // The review sentence: the name the module was not given comes from here.
  const reviewVars: Record<string, TermsVar> | undefined = review?.vars && {
    ...review.vars,
    ...(providerName && review.vars.name?.kind === 'text' && !review.vars.name.value
      ? { name: { kind: 'text', value: providerName } as TermsVar }
      : {}),
  };
  const reviewText = review ? keyText(review.key, reviewVars) : '';
  const reviewWord = review ? toneWord(review.tone) : '';
  // `read` and its variants that also say whose words the facts are (termsReviewLine).
  const read = review?.key.startsWith('offer_terms.review.read') ?? false;

  if (visible.length === 0 && !reviewText) return null;

  return (
    <div className={className}>
      {review && !read && (
        <p
          className={cn(
            'mb-1.5 font-semibold',
            page ? 'text-sm md:text-base' : 'text-[13px] leading-5',
            TONE_TEXT[review.tone]
          )}
        >
          {reviewText}
          {reviewWord && <span className="sr-only">{` (${reviewWord})`}</span>}
        </p>
      )}
      {visible.length > 0 && (
        <dl className={DL[variant]}>
          {visible.map((row) => {
            const label = t(row.label);
            const word = toneWord(row.tone);
            const smart = row.question === 'smartChargingRequired' && onSmartInfo !== undefined;
            return (
              <React.Fragment key={`${keyPrefix}-${row.question}`}>
                <dt className={DT[variant]}>
                  {smart ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        // The row beneath is clickable: the modal's button keeps its own click.
                        e.stopPropagation();
                        onSmartInfo?.();
                      }}
                      aria-haspopup="dialog"
                      className="rounded-sm text-left transition-colors hover:text-emerald-400 focus-ring"
                    >
                      {label}
                      <span aria-hidden="true">*</span>{' '}
                      {/* The space above is a text node of its own: a name computation may trim an element's edge. */}
                      <span className="sr-only">{`(${t('table.smart_charging_info_aria')})`}</span>
                    </button>
                  ) : (
                    label
                  )}
                </dt>
                <dd className={cn(DD, TONE_TEXT[row.tone])}>
                  {text(row.value)}
                  {word && <span className="sr-only">{` (${word})`}</span>}
                  {row.note && <span className={NOTE}>{` (${row.note})`}</span>}
                </dd>
              </React.Fragment>
            );
          })}
        </dl>
      )}
      {review && read && (
        <p
          className={cn(
            'font-medium text-fg-muted',
            page ? 'text-xs leading-5' : 'text-[11px] leading-4',
            // The air between the table and the line that closes it (the legend used
            // to carry it); a line that stands alone, without rows, needs none.
            visible.length > 0 && (page ? 'mt-3' : 'mt-2')
          )}
        >
          {reviewText}
        </p>
      )}
    </div>
  );
};
